import {getPrimaryAsset} from '@sollapay/domain/asset-types';
import {Typography, nodeColumn} from '@sollapay/ui/components';

import {AmountCell} from '@/components';

import {formatPurchaseAssetNames, getAmountToPayLabel} from '../utils';
import {BuyerPaymentStatusBadge} from './BuyerPaymentStatusBadge';
import {BuyerRegulatoryReportsBadge} from './BuyerRegulatoryReportsBadge';

import type {BuyerPaymentStatus} from '../types/buyers.types';
import type {TrustTypeStrategy} from '@sollapay/domain/trust-types';
import type {BuyerPurchaseDTO} from '@sollapay/types';
import type {TFunction} from 'i18next';
import type {FC} from 'react';

interface PurchaseRowLike {
  purchase: BuyerPurchaseDTO;
  paymentStatus: BuyerPaymentStatus;
  amountToPayNis: string;
  depositPaidNis: string;
}

export function createAssetColumn<T extends PurchaseRowLike>(t: TFunction, sortable: boolean) {
  return nodeColumn<T>({
    id: 'asset',
    header: t('view.buyers.list.columns.asset'),
    cell: row => <AssetCell purchase={row.purchase} t={t} />,
    ...(sortable
      ? {maxWidth: 200, accessorFn: (row: T) => getPrimaryAsset(row.purchase).displayCode}
      : {enableSorting: false})
  });
}

export function createPaymentStatusColumn<T extends PurchaseRowLike>(
  t: TFunction,
  sortable: boolean
) {
  return nodeColumn<T>({
    id: 'paymentStatus',
    header: t('view.buyers.list.columns.paymentStatus'),
    cell: row => <BuyerPaymentStatusBadge status={row.paymentStatus} />,
    ...(sortable ? {accessorFn: (row: T) => row.paymentStatus} : {enableSorting: false})
  });
}

export function createPurchasePriceColumn<T extends PurchaseRowLike>(
  t: TFunction,
  sortable: boolean
) {
  return nodeColumn<T>({
    id: 'purchasePrice',
    header: t('view.buyers.list.columns.purchasePrice'),
    cell: row => <AmountCell amount={row.purchase.purchasePriceNis} />,
    ...(sortable ? {accessorFn: (row: T) => row.purchase.purchasePriceNis} : {enableSorting: false})
  });
}

export function createAmountToPayColumn<T extends PurchaseRowLike>(
  strategy: TrustTypeStrategy,
  t: TFunction,
  sortable: boolean
) {
  return nodeColumn<T>({
    id: 'amountToPay',
    header: getAmountToPayLabel(strategy, t),
    cell: row => <AmountCell amount={row.amountToPayNis} />,
    ...(sortable ? {accessorFn: (row: T) => row.amountToPayNis} : {enableSorting: false})
  });
}

export function createDepositPaidColumn<T extends PurchaseRowLike>(
  t: TFunction,
  sortable: boolean
) {
  return nodeColumn<T>({
    id: 'depositPaid',
    header: t('view.buyers.list.columns.depositPaid'),
    cell: row => <AmountCell amount={row.depositPaidNis} />,
    ...(sortable ? {accessorFn: (row: T) => row.depositPaidNis} : {enableSorting: false})
  });
}

export function createRegulatoryReportsColumn<T extends PurchaseRowLike>(
  t: TFunction,
  trustAccountId: string
) {
  return nodeColumn<T>({
    id: 'regulatoryReports',
    header: t('view.buyers.list.columns.regulatoryReports'),
    enableSorting: false,
    cell: row => (
      <BuyerRegulatoryReportsBadge purchase={row.purchase} trustAccountId={trustAccountId} />
    )
  });
}

const AssetCell: FC<{purchase: BuyerPurchaseDTO; t: TFunction}> = ({purchase, t}) => {
  const label = formatPurchaseAssetNames(purchase.assets, t);
  return (
    <Typography size="sm" title={label}>
      {label}
    </Typography>
  );
};
